import React, { useState, useEffect, useContext } from 'react';
import { Box, Button, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Chip, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { UserContext } from '../contexts/UserContext';
import { showSuccess, showError, showConfirmationAlert } from '../utils/Alerts';
import AddVehicleModal from './AddVehicleModal';
import BulkVehicleUpload from './BulkVehicleUpload';
import { calculateDaysOnLot } from '../utils/timeUtils';
import { Spinner } from './Spinner';
import { useNavigate } from 'react-router-dom';

export default function VehicleInventory() {
    const navigate = useNavigate();
    const { user, token } = useContext(UserContext);
    const [loaded, setLoaded] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
    const [vehicles, setVehicles] = useState([]);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [updatedRecord, setUpdatedRecord] = useState(null);
    const [filters, setFilters] = useState({
        status: 'all',
        make: 'all',
        priceRange: 'all',
        sortBy: 'newest',
    });

    useEffect(() => {
        const setUpdateVehicle = (updatedRecord) => {
            setVehicles((prevVehicles) => {
                return prevVehicles.map((vehicle) => {
                    if (vehicle.vehicleID === updatedRecord?.vehicleID) {
                        return updatedRecord;
                    }
                    return vehicle;
                });
            });
        };
        setUpdateVehicle(updatedRecord);
    }, [updatedRecord]);

    useEffect(() => {
        // Simulate loading with mock data
        if (user && token) {
            setLoaded(true);
            setTimeout(() => {
                // Mock initial data - empty for now
                setVehicles([]);
                setLoaded(false);
            }, 500);
        }
    }, [user, token, navigate]);

    const handleOpenAddModal = () => {
        setSelectedVehicle(null);
        setIsAddModalOpen(true);
    };

    const handleCloseAddModal = () => {
        setIsAddModalOpen(false);
        setSelectedVehicle(null);
    };

    const handleOpenBulkModal = () => {
        setIsBulkModalOpen(true);
    };

    const handleCloseBulkModal = () => {
        setIsBulkModalOpen(false);
    };

    const handleEditVehicle = (vehicle) => {
        setSelectedVehicle(vehicle);
        setIsAddModalOpen(true);
    };

    const addVehicle = (newVehicle) => {
        setVehicles([...vehicles, newVehicle]);
    };

    const handleDeleteVehicle = (vehicle) => {
        const confirmationMessage = `Are you sure you want to delete ${vehicle.year} ${vehicle.make} ${vehicle.model}?`;

        showConfirmationAlert({
            confirmationMessage,
            onConfirm: () => {
                // Remove from local state
                setVehicles(vehicles.filter(v => v.vehicleID !== vehicle.vehicleID));
                showSuccess('Vehicle deleted successfully');
            },
            onCancel: () => { },
        });
    };

    const handleStatusChange = (vehicle, newStatus) => {
        const confirmationMessage = `Change status to ${newStatus}?`;

        showConfirmationAlert({
            confirmationMessage,
            onConfirm: () => {
                // Update local state
                const updatedVehicle = { ...vehicle, status: newStatus };
                setUpdatedRecord(updatedVehicle);
                showSuccess(`Status updated to ${newStatus}`);
            },
            onCancel: () => { },
        });
    };

    const getFilteredVehicles = () => {
        let filtered = [...vehicles];

        if (filters.status !== 'all') {
            filtered = filtered.filter(v => v.status === filters.status);
        }

        if (filters.make !== 'all') {
            filtered = filtered.filter(v => v.make === filters.make);
        }

        if (filters.priceRange !== 'all') {
            const ranges = {
                '0-20000': [0, 20000],
                '20000-40000': [20000, 40000],
                '40000-60000': [40000, 60000],
                '60000+': [60000, Infinity],
            };
            const [min, max] = ranges[filters.priceRange] || [0, Infinity];
            filtered = filtered.filter(v => v.price >= min && v.price < max);
        }

        switch (filters.sortBy) {
            case 'newest':
                filtered.sort((a, b) => new Date(b.dateAdded) - new Date(a.dateAdded));
                break;
            case 'oldest':
                filtered.sort((a, b) => new Date(a.dateAdded) - new Date(b.dateAdded));
                break;
            case 'price-low':
                filtered.sort((a, b) => a.price - b.price);
                break;
            case 'price-high':
                filtered.sort((a, b) => b.price - a.price);
                break;
            case 'mileage-low':
                filtered.sort((a, b) => a.mileage - b.mileage);
                break;
            case 'mileage-high':
                filtered.sort((a, b) => b.mileage - a.mileage);
                break;
            default:
                break;
        }

        return filtered;
    };

    const getMakes = () => {
        const makes = [...new Set(vehicles.map(v => v.make))];
        return makes.sort();
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'available':
                return 'success';
            case 'reserved':
                return 'warning';
            case 'sold':
                return 'error';
            default:
                return 'default';
        }
    };

    const getAgingColor = (days) => {
        if (days < 30) return '#4caf50';
        if (days < 60) return '#ff9800';
        return '#f44336';
    };

    const filteredVehicles = getFilteredVehicles();

    return (
        <>
            {loaded ? (<div className="middle"><Spinner /></div>) : (
                <>
                    <Grid container justifyContent="center">
                        <Grid item xs={12} md={11} lg={10} sx={{ mt: { xs: 3, sm: 6 }, px: { xs: 2, sm: 0 } }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    border: '1px solid #c52b2b',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                }}
                            >
                                <Box
                                    sx={{
                                        bgcolor: '#c52b2b',
                                        color: 'white',
                                        p: 2,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                        gap: 2
                                    }}
                                >
                                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                        Vehicle Inventory
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                            variant="contained"
                                            onClick={handleOpenBulkModal}
                                            sx={{
                                                bgcolor: 'white',
                                                color: '#c52b2b',
                                                '&:hover': { bgcolor: '#f5f5f5' },
                                            }}
                                        >
                                            Bulk Upload
                                        </Button>
                                        <Button
                                            variant="contained"
                                            onClick={handleOpenAddModal}
                                            sx={{
                                                bgcolor: 'white',
                                                color: '#c52b2b',
                                                '&:hover': { bgcolor: '#f5f5f5' },
                                            }}
                                        >
                                            Add Vehicle
                                        </Button>
                                    </Box>
                                </Box>

                                <Grid container spacing={2} sx={{ p: 3 }}>
                                    <Grid item xs={6} sm={3}>
                                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                                            <Typography variant="h4" sx={{ color: '#c52b2b', fontWeight: 600 }}>
                                                {vehicles.length}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                Total Inventory
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                                            <Typography variant="h4" sx={{ color: '#4caf50', fontWeight: 600 }}>
                                                {vehicles.filter(v => v.status === 'available').length}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                Available
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                                            <Typography variant="h4" sx={{ color: '#ff9800', fontWeight: 600 }}>
                                                {vehicles.filter(v => v.status === 'reserved').length}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                Reserved
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                                            <Typography variant="h4" sx={{ color: '#c52b2b', fontWeight: 600 }}>
                                                ${vehicles.reduce((sum, v) => sum + (v.price || 0), 0).toLocaleString()}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                Total Value
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>

                                <Box sx={{ px: 3, pb: 2 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6} sm={3}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Status</InputLabel>
                                                <Select
                                                    value={filters.status}
                                                    label="Status"
                                                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                                                >
                                                    <MenuItem value="all">All Status</MenuItem>
                                                    <MenuItem value="available">Available</MenuItem>
                                                    <MenuItem value="reserved">Reserved</MenuItem>
                                                    <MenuItem value="sold">Sold</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Make</InputLabel>
                                                <Select
                                                    value={filters.make}
                                                    label="Make"
                                                    onChange={(e) => setFilters({ ...filters, make: e.target.value })}
                                                >
                                                    <MenuItem value="all">All Makes</MenuItem>
                                                    {getMakes().map(make => (
                                                        <MenuItem key={make} value={make}>{make}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Price Range</InputLabel>
                                                <Select
                                                    value={filters.priceRange}
                                                    label="Price Range"
                                                    onChange={(e) => setFilters({ ...filters, priceRange: e.target.value })}
                                                >
                                                    <MenuItem value="all">All Prices</MenuItem>
                                                    <MenuItem value="0-20000">$0 - $20,000</MenuItem>
                                                    <MenuItem value="20000-40000">$20,000 - $40,000</MenuItem>
                                                    <MenuItem value="40000-60000">$40,000 - $60,000</MenuItem>
                                                    <MenuItem value="60000+">$60,000+</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Sort By</InputLabel>
                                                <Select
                                                    value={filters.sortBy}
                                                    label="Sort By"
                                                    onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
                                                >
                                                    <MenuItem value="newest">Newest First</MenuItem>
                                                    <MenuItem value="oldest">Oldest First</MenuItem>
                                                    <MenuItem value="price-low">Price: Low to High</MenuItem>
                                                    <MenuItem value="price-high">Price: High to Low</MenuItem>
                                                    <MenuItem value="mileage-low">Mileage: Low to High</MenuItem>
                                                    <MenuItem value="mileage-high">Mileage: High to Low</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </Box>

                                <TableContainer>
                                    <Table>
                                        <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600 }}>Vehicle</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>VIN</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Price</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Mileage</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Days on Lot</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {filteredVehicles.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                                        <Typography color="textSecondary">
                                                            No vehicles found. Add your first vehicle to get started!
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                filteredVehicles.map((vehicle) => {
                                                    const daysOnLot = calculateDaysOnLot(vehicle.dateAdded);
                                                    return (
                                                        <TableRow key={vehicle.vehicleID} hover>
                                                            <TableCell>
                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                    {vehicle.year} {vehicle.make} {vehicle.model}
                                                                </Typography>
                                                                <Typography variant="caption" color="textSecondary">
                                                                    {vehicle.trim}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2">{vehicle.vin}</Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2">${vehicle.price?.toLocaleString()}</Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2">{vehicle.mileage?.toLocaleString()} mi</Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={vehicle.status}
                                                                    color={getStatusColor(vehicle.status)}
                                                                    size="small"
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={`${daysOnLot} days`}
                                                                    size="small"
                                                                    sx={{
                                                                        bgcolor: getAgingColor(daysOnLot),
                                                                        color: 'white'
                                                                    }}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                                    <Button
                                                                        size="small"
                                                                        onClick={() => handleEditVehicle(vehicle)}
                                                                        sx={{ minWidth: 'auto', fontSize: '0.75rem' }}
                                                                    >
                                                                        Edit
                                                                    </Button>
                                                                    {vehicle.status !== 'sold' && (
                                                                        <Button
                                                                            size="small"
                                                                            onClick={() => handleStatusChange(vehicle, vehicle.status === 'available' ? 'reserved' : 'available')}
                                                                            sx={{ minWidth: 'auto', fontSize: '0.75rem' }}
                                                                        >
                                                                            {vehicle.status === 'available' ? 'Reserve' : 'Available'}
                                                                        </Button>
                                                                    )}
                                                                    {vehicle.status !== 'sold' && (
                                                                        <Button
                                                                            size="small"
                                                                            onClick={() => handleStatusChange(vehicle, 'sold')}
                                                                            sx={{ minWidth: 'auto', fontSize: '0.75rem' }}
                                                                        >
                                                                            Sold
                                                                        </Button>
                                                                    )}
                                                                    <Button
                                                                        size="small"
                                                                        color="error"
                                                                        onClick={() => handleDeleteVehicle(vehicle)}
                                                                        sx={{ minWidth: 'auto', fontSize: '0.75rem' }}
                                                                    >
                                                                        Delete
                                                                    </Button>
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Grid>
                    </Grid>

                    <AddVehicleModal
                        open={isAddModalOpen}
                        handleClose={handleCloseAddModal}
                        vehicle={selectedVehicle}
                        addVehicle={addVehicle}
                        setUpdatedRecord={setUpdatedRecord}
                        existVehicles={vehicles}
                    />

                    <BulkVehicleUpload
                        open={isBulkModalOpen}
                        handleClose={handleCloseBulkModal}
                        setVehicles={setVehicles}
                    />
                </>
            )}
        </>
    );
}
